import React, { useState, useEffect, useRef } from 'react';
import { websiteAnalyticsService } from '../../services/websiteAnalyticsService';
import { HeatmapData, ClickPoint, WebsiteAnalyticsFilters } from '../../types/websiteAnalytics';

interface HeatmapVisualizationProps {
  page: string;
  filters?: WebsiteAnalyticsFilters;
  refreshInterval?: number;
}

export const HeatmapVisualization: React.FC<HeatmapVisualizationProps> = ({
  page,
  filters,
  refreshInterval = 300000
}) => {
  const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<'clicks' | 'scroll'>('clicks');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchHeatmapData = async () => {
    try {
      setLoading(true);
      const response = await websiteAnalyticsService.getHeatmapData(page, filters);
      
      if (response.success && response.data) {
        setHeatmapData(response.data);
        setError(null);
      } else {
        setError('Failed to fetch heatmap data');
      }
    } catch (err) {
      setError('Error loading heatmap data');
      console.error('Error fetching heatmap data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeatmapData();
    
    const interval = setInterval(fetchHeatmapData, refreshInterval);
    return () => clearInterval(interval);
  }, [page, filters, refreshInterval]);

  useEffect(() => {
    if (heatmapData && canvasRef.current) {
      drawHeatmap();
    }
  }, [heatmapData, selectedView]);

  const drawHeatmap = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    
    if (!canvas || !container || !heatmapData) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const containerRect = container.getBoundingClientRect();
    canvas.width = containerRect.width;
    canvas.height = 600; // Fixed height for demo

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (selectedView === 'clicks') {
      drawClickHeatmap(ctx, canvas.width, canvas.height);
    } else {
      drawScrollHeatmap(ctx, canvas.width, canvas.height);
    }
  };

  const drawClickHeatmap = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    if (!heatmapData) return;

    // Safety check for clicks array
    const clicks = heatmapData.clicks || [];
    if (clicks.length === 0) {
      // Draw "No data" message
      ctx.fillStyle = 'rgba(240, 240, 240, 0.5)';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = '#666';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No click data available for this page', width / 2, height / 2);
      return;
    }

    // Find max click count for normalization
    // Handle both old format (count) and new format (clicks)
    const maxClicks = Math.max(...clicks.map(click => click.count || click.clicks || 0), 1);
    
    // Draw background
    ctx.fillStyle = 'rgba(240, 240, 240, 0.3)';
    ctx.fillRect(0, 0, width, height);

    // Draw click points - handle both coordinate-based and element-based data
    clicks.forEach((click, index) => {
      const clickCount = click.count || click.clicks || 0;
      const intensity = clickCount / maxClicks;
      const radius = Math.max(20, Math.min(60, clickCount * 3 + 15));
      
      // If we have x,y coordinates, use them; otherwise distribute evenly
      let x, y;
      if (click.x !== undefined && click.y !== undefined && (click.x > 0 || click.y > 0)) {
        // API returns coordinates in pixels (grouped by 50px grid)
        // Scale to canvas width/height
        const maxX = 1920; // Assume max viewport width
        const maxY = 1080; // Assume max viewport height
        x = Math.min((click.x / maxX) * width, width - radius);
        y = Math.min((click.y / maxY) * height, height - radius);
        // Ensure minimum position
        x = Math.max(radius, x);
        y = Math.max(radius, y);
      } else {
        // Distribute clicks in a grid pattern for element-based data
        const cols = Math.ceil(Math.sqrt(clicks.length));
        const col = index % cols;
        const row = Math.floor(index / cols);
        const cellWidth = width / cols;
        const cellHeight = (height - 100) / Math.ceil(clicks.length / cols);
        x = (col + 0.5) * cellWidth;
        y = (row + 0.5) * cellHeight + 50;
      }

      // Create radial gradient for heat effect
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      
      if (intensity > 0.8) {
        gradient.addColorStop(0, `rgba(255, 0, 0, ${0.8 * intensity})`);
        gradient.addColorStop(0.5, `rgba(255, 100, 0, ${0.4 * intensity})`);
        gradient.addColorStop(1, `rgba(255, 200, 0, ${0.1 * intensity})`);
      } else if (intensity > 0.5) {
        gradient.addColorStop(0, `rgba(255, 100, 0, ${0.8 * intensity})`);
        gradient.addColorStop(0.5, `rgba(255, 200, 0, ${0.4 * intensity})`);
        gradient.addColorStop(1, `rgba(255, 255, 0, ${0.1 * intensity})`);
      } else {
        gradient.addColorStop(0, `rgba(0, 200, 100, ${0.8 * intensity})`);
        gradient.addColorStop(0.5, `rgba(100, 255, 100, ${0.4 * intensity})`);
        gradient.addColorStop(1, `rgba(200, 255, 200, ${0.1 * intensity})`);
      }

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fill();

      // Add element name and click count label
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth = 3;
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'center';
      
      // Format the element name for display
      let displayLabel = click.element || 'Click';
      
      // Map common HTML tags to friendly names for canvas display
      const tagMappings: Record<string, string> = {
        'button': '🔘 Button',
        'a': '🔗 Link',
        'div': '📦 Section',
        'span': '📝 Text',
        'img': '🖼️ Image',
        'input': '✏️ Input',
        'select': '📋 Dropdown',
        'nav': '🧭 Nav',
        'svg': '🎨 Icon',
        'path': '🎨 Icon',
        'i': '🎨 Icon',
      };
      
      const lowerLabel = displayLabel.toLowerCase();
      if (tagMappings[lowerLabel]) {
        displayLabel = tagMappings[lowerLabel];
      } else if (displayLabel.length > 20) {
        displayLabel = displayLabel.substring(0, 18) + '...';
      }
      
      // Draw label with background for better readability
      const labelWidth = ctx.measureText(displayLabel).width + 10;
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillRect(x - labelWidth/2, y - 20, labelWidth, 16);
      
      ctx.fillStyle = 'white';
      ctx.fillText(displayLabel, x, y - 8);
      
      // Draw click count
      ctx.font = 'bold 10px Arial';
      const clickText = `${clickCount} click${clickCount !== 1 ? 's' : ''}`;
      const clickWidth = ctx.measureText(clickText).width + 8;
      ctx.fillStyle = intensity > 0.5 ? 'rgba(220,38,38,0.9)' : 'rgba(34,197,94,0.9)';
      ctx.fillRect(x - clickWidth/2, y + 2, clickWidth, 14);
      ctx.fillStyle = 'white';
      ctx.fillText(clickText, x, y + 12);
    });
  };

  const drawScrollHeatmap = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    if (!heatmapData) return;

    // Safety check for scrollDepth array
    const scrollDepth = heatmapData.scrollDepth || [];
    if (scrollDepth.length === 0) return;

    // Draw background
    ctx.fillStyle = 'rgba(240, 240, 240, 0.3)';
    ctx.fillRect(0, 0, width, height);

    // Find max sessions for normalization
    const maxSessions = Math.max(...scrollDepth.map(scroll => scroll.sessions || 0), 1);

    // Draw scroll depth bars
    scrollDepth.forEach((scroll, index) => {
      const intensity = scroll.sessions / maxSessions;
      const barHeight = (scroll.depth / 100) * height;
      
      // Color based on scroll percentage
      let color;
      if (scroll.percentage > 80) {
        color = `rgba(0, 255, 0, ${0.3 + intensity * 0.7})`;
      } else if (scroll.percentage > 50) {
        color = `rgba(255, 255, 0, ${0.3 + intensity * 0.7})`;
      } else {
        color = `rgba(255, 0, 0, ${0.3 + intensity * 0.7})`;
      }

      ctx.fillStyle = color;
      ctx.fillRect(0, barHeight, width, 20);

      // Add percentage label
      ctx.fillStyle = 'black';
      ctx.font = '12px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`${scroll.percentage}% (${scroll.sessions} sessions)`, 10, barHeight + 15);
    });
  };

  const getIntensityColor = (intensity: number): string => {
    if (intensity > 0.8) return 'bg-red-500';
    if (intensity > 0.6) return 'bg-orange-500';
    if (intensity > 0.4) return 'bg-yellow-500';
    if (intensity > 0.2) return 'bg-green-500';
    return 'bg-blue-500';
  };

  // Format element name for user-friendly display
  const formatElementName = (element: string | undefined): string => {
    if (!element) return 'Unknown Element';
    
    // If it's already a readable name (has spaces or is capitalized), return as-is
    if (element.includes(' ') || /^[A-Z]/.test(element)) {
      return element;
    }
    
    // Map common HTML tags to friendly names
    const tagMappings: Record<string, string> = {
      'button': 'Button',
      'a': 'Link',
      'div': 'Section',
      'span': 'Text',
      'img': 'Image',
      'input': 'Input Field',
      'select': 'Dropdown',
      'textarea': 'Text Area',
      'form': 'Form',
      'nav': 'Navigation',
      'header': 'Header',
      'footer': 'Footer',
      'section': 'Section',
      'article': 'Article',
      'aside': 'Sidebar',
      'main': 'Main Content',
      'li': 'List Item',
      'ul': 'List',
      'p': 'Paragraph',
      'h1': 'Heading',
      'h2': 'Heading',
      'h3': 'Heading',
      'svg': 'Icon',
      'path': 'Icon',
      'i': 'Icon',
    };
    
    const lowerElement = element.toLowerCase();
    if (tagMappings[lowerElement]) {
      return tagMappings[lowerElement];
    }
    
    // Convert camelCase or kebab-case to readable text
    return element
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading && !heatmapData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchHeatmapData}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!heatmapData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No heatmap data available for this page</p>
      </div>
    );
  }

  const clicks = heatmapData.clicks || [];
  const maxClicks = clicks.length > 0 ? Math.max(...clicks.map(click => click.count || click.clicks || 0)) : 0;

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Heatmap for: {page}</h3>
            <p className="text-sm text-gray-600">
              Based on {heatmapData.totalSessions} sessions • Avg time: {Math.round(heatmapData.timeOnPage)}s
            </p>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedView('clicks')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                selectedView === 'clicks'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Click Heatmap
            </button>
            <button
              onClick={() => setSelectedView('scroll')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                selectedView === 'scroll'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Scroll Depth
            </button>
          </div>
        </div>
      </div>

      {/* Heatmap Visualization */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">
              {selectedView === 'clicks' ? 'Click Heatmap' : 'Scroll Depth Visualization'}
            </h4>
            
            {/* Legend */}
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Intensity:</span>
              <div className="flex items-center space-x-1">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span className="text-xs">Low</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-xs">Medium</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-xs">High</span>
              </div>
            </div>
          </div>
        </div>
        
        <div ref={containerRef} className="relative">
          <canvas
            ref={canvasRef}
            className="w-full border-b border-gray-200"
            style={{ maxHeight: '600px' }}
          />
        </div>
      </div>

      {/* Click Statistics */}
      {selectedView === 'clicks' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-semibold mb-4">🖱️ Most Clicked Elements</h4>
            <p className="text-sm text-gray-500 mb-4">Shows what visitors click on most</p>
            <div className="space-y-3">
              {clicks
                .sort((a, b) => (b.count || b.clicks || 0) - (a.count || a.clicks || 0))
                .slice(0, 5)
                .map((click, index) => {
                  const clickCount = click.count || click.clicks || 0;
                  const intensity = clickCount / maxClicks;
                  const totalClicks = clicks.reduce((sum, c) => sum + (c.count || c.clicks || 0), 0);
                  const percentage = totalClicks > 0 ? ((clickCount / totalClicks) * 100).toFixed(1) : '0';
                  
                  // Format element name for display
                  const elementName = formatElementName(click.element);
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-sm font-bold text-gray-600">
                          {index + 1}
                        </div>
                        <div className={`w-3 h-3 rounded-full ${getIntensityColor(intensity)}`}></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {elementName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {clickCount} {clickCount === 1 ? 'click' : 'clicks'} • {percentage}% of total
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className={`h-2 rounded-full ${intensity > 0.6 ? 'bg-red-500' : intensity > 0.3 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.max(5, intensity * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              {clicks.length === 0 && (
                <p className="text-gray-500 text-center py-4">No click data yet. Visitors need to interact with the website.</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-semibold mb-4">📊 Click Summary</h4>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Clicks:</span>
                <span className="font-medium">
                  {clicks.reduce((sum, click) => sum + (click.count || click.clicks || 0), 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Click Areas:</span>
                <span className="font-medium">{clicks.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg Clicks per Area:</span>
                <span className="font-medium">
                  {clicks.length > 0 
                    ? (clicks.reduce((sum, click) => sum + (click.count || click.clicks || 0), 0) / clicks.length).toFixed(1)
                    : '0'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Most Active Area:</span>
                <span className="font-medium">{maxClicks} clicks</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll Statistics */}
      {selectedView === 'scroll' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-semibold">Scroll Depth Analysis</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scroll Depth
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Percentage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Drop-off Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(heatmapData.scrollDepth || []).map((scroll, index) => {
                  const scrollDepthArray = heatmapData.scrollDepth || [];
                  const dropOffRate = index > 0 
                    ? ((scrollDepthArray[index - 1].sessions - scroll.sessions) / scrollDepthArray[index - 1].sessions) * 100
                    : 0;
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {scroll.depth}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {scroll.sessions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {scroll.percentage.toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index > 0 ? `${dropOffRate.toFixed(1)}%` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};